import { v4 as uuidv4 } from "uuid";
import {
  JsaBasicInfo,
  JsaRacInfo,
  JsaTrainingInfo,
  JsaNonEmployeeSignaturesInfo,
  JsaEmployeeSignaturesInfo,
} from "./core";
import { JsaHazardsV2, JsaControlMeasuresV2 } from "./v2";

// JsaV2
export class JsaV2 {
  constructor(data = {}) {
    this.id = data?.basic_info?.id || uuidv4();
    this.jsaBasicInfo = new JsaBasicInfo(data.basic_info || {});
    // Ensure ID from backend is preserved on the basic info instance
    if (data?.basic_info && typeof data.basic_info === "object") {
      this.jsaBasicInfo.id = data.basic_info.id || this.jsaBasicInfo.id;
    }
    this.jsaRacInfo = new JsaRacInfo(data.hazard_rac_assessment || {});
    this.jsaTrainingInfo = new JsaTrainingInfo(data.training || {});

    this.jsaNonEmployeeSignaturesInfo = data.non_employee_signatures
      ? data.non_employee_signatures.map(
          (signature) => new JsaNonEmployeeSignaturesInfo(signature)
        )
      : [];
    this.jsaEmployeeSignaturesInfo = data.signatures
      ? data.signatures.map(
          (signature) => new JsaEmployeeSignaturesInfo(signature)
        )
      : [];
    // These are the specific sections for JSA V2
    this.jsaHazardsV2 = new JsaHazardsV2(data.hazards || {});
    this.jsaControlMeasuresV2 = new JsaControlMeasuresV2(
      data.control_measures || {}
    );
  }

  /**
   * Creates a deep clone of the JsaV2 instance.
   * @returns {JsaV2} A new JsaV2 instance.
   */
  clone() {
    // Rebuild using constructor-expected keys. Avoid basicInfo.toJson() because
    // it uses legacy DEL keys that don't match the constructor.
    const basic = this.jsaBasicInfo || {};
    const basic_info = {
      id: basic.id,
      jsa_client_name: basic.jsaClientName ?? "",
      supervisor_id: basic?.supervisor?.value ?? "",
      supervisor_name: basic?.supervisor?.label ?? "",
      jsa_completed_date: basic.jsaCompletedDate || null,
      jsa_completed_time: basic.jsaCompletedTime || "",
      jsa_job_description: basic.jsaJobDescription ?? "",
      site_location: basic?.siteLocation?.label || null,
      medical_facility: basic.medicalFacility ?? "",
    };

    // For the rest, the .toJson() shapes are already aligned with constructor expectations
    const data = {
      basic_info,
      hazard_rac_assessment:
        typeof this.jsaRacInfo?.toJson === "function"
          ? this.jsaRacInfo.toJson()
          : {},
      training:
        typeof this.jsaTrainingInfo?.toJson === "function"
          ? this.jsaTrainingInfo.toJson()
          : {},
      non_employee_signatures: Array.isArray(this.jsaNonEmployeeSignaturesInfo)
        ? this.jsaNonEmployeeSignaturesInfo.map((sig) => {
            const id = sig?.rowId;
            const base = typeof sig?.toJson === "function" ? sig.toJson() : {};
            return { id, ...base };
          })
        : [],
      // Rebuild signatures with constructor-expected keys
      signatures: Array.isArray(this.jsaEmployeeSignaturesInfo)
        ? this.jsaEmployeeSignaturesInfo.map((sig) => {
            const id = sig?.rowId;
            const employee_id = sig?.employeeId ?? "";
            const employee_name = sig?.employeeName ?? "";
            const raw =
              typeof sig?.employeeSig === "string" ? sig.employeeSig : "";
            const employee_sig = raw
              ? raw.replace("data:image/png;base64,", "").trim()
              : "";
            return { id, employee_id, employee_name, employee_sig };
          })
        : [],
      hazards:
        typeof this.jsaHazardsV2?.toJson === "function"
          ? this.jsaHazardsV2.toJson()
          : {},
      control_measures:
        typeof this.jsaControlMeasuresV2?.toJson === "function"
          ? this.jsaControlMeasuresV2.toJson()
          : {},
    };

    return new JsaV2(data);
  }

  /**
   * Applies a patch received from a websocket broadcast.
   * This method ensures immutability by cloning the JSA before applying changes.
   * @param {object} patch The patch data from the server.
   * @returns {JsaV2} A new, updated JsaV2 instance.
   */
  applyPatch(patch) {
    const newJsa = this.clone();
    const { updated_fields } = patch;

    // Handle signatures update (list payload)
    if (
      patch?.action === "jsa_signatures_update" &&
      Array.isArray(updated_fields)
    ) {
      // Replace or upsert rows by id, preserving rowId
      const byId = new Map(
        (newJsa.jsaEmployeeSignaturesInfo || []).map((r) => [r.rowId, r])
      );
      for (const sig of updated_fields) {
        const id = sig?.id;
        const employee_id = sig?.employee_id ?? "";
        const employee_name = sig?.employee_name ?? "";
        const employee_sig = sig?.employee_sig || "";
        const existing = byId.get(id);
        if (existing) {
          existing.employeeId = employee_id;
          existing.employeeName = employee_name;
          existing.employeeSig = employee_sig;
        } else if (id) {
          byId.set(
            id,
            new JsaEmployeeSignaturesInfo({
              id,
              employee_id,
              employee_name,
              employee_sig: sig?.employee_sig || "",
            })
          );
        }
      }
      newJsa.jsaEmployeeSignaturesInfo = Array.from(byId.values());
      return newJsa;
    }

    // Handle signatures delete (single id payload)
    if (patch?.action === "jsa_signatures_delete") {
      const delId =
        (updated_fields && typeof updated_fields === "object"
          ? updated_fields.id
          : null) || null;
      if (delId) {
        newJsa.jsaEmployeeSignaturesInfo = (
          newJsa.jsaEmployeeSignaturesInfo || []
        ).filter((row) => row.rowId !== delId);
      }
      return newJsa;
    }

    // Support both legacy nested shape and flat shape from websocket broadcasts.
    // 1) Nested: { updated_fields: { jsaBasicInfo: { jsaCompletedDate: "YYYY-MM-DD" } } }
    // 2) Flat:   { updated_fields: { jsa_completed_date: "YYYY-MM-DD", ... } }

    const isNested = Object.keys(updated_fields || {}).some((k) =>
      [
        "jsaBasicInfo",
        "jsaRacInfo",
        "jsaHazardsV2",
        "jsaControlMeasuresV2",
      ].includes(k)
    );

    if (isNested) {
      for (const modelKey in updated_fields) {
        if (Object.hasOwnProperty.call(newJsa, modelKey)) {
          const fieldsToUpdate = updated_fields[modelKey];
          for (const fieldKey in fieldsToUpdate) {
            if (
              Object.hasOwnProperty.call(newJsa[modelKey], fieldKey) ||
              typeof newJsa[modelKey][fieldKey] !== "undefined"
            ) {
              newJsa[modelKey][fieldKey] = fieldsToUpdate[fieldKey];
            }
          }
        }
      }
      return newJsa;
    }

    // Flat mapping for basic info / hazards / control measures
    const basicInfoMap = {
      jsa_client_name: (v) => (newJsa.jsaBasicInfo.jsaClientName = v ?? ""),
      jsa_job_description: (v) =>
        (newJsa.jsaBasicInfo.jsaJobDescription = v ?? ""),
      jsa_completed_date: (v) =>
        (newJsa.jsaBasicInfo.jsaCompletedDate = v || null),
      jsa_completed_time: (v) =>
        (newJsa.jsaBasicInfo.jsaCompletedTime = v || ""),
      site_location: (v) =>
        (newJsa.jsaBasicInfo.siteLocation = v
          ? { label: v, value: "", lat: "", lng: "" }
          : null),
      medical_facility: (v) => (newJsa.jsaBasicInfo.medicalFacility = v ?? ""),
      supervisor_id: (v) => {
        // backend sends { supervisor_id, supervisor_name }
        if (v && typeof v === "object") {
          newJsa.jsaBasicInfo.supervisor = {
            value: v.supervisor_id ?? "",
            label: v.supervisor_name ?? "",
          };
        } else {
          newJsa.jsaBasicInfo.supervisor = { value: v ?? "", label: "" };
        }
      },
    };

    // Flat mapping for RAC (JHA) text/code fields
    const racMap = {
      rac_access_pot_haz_info: (v) =>
        (newJsa.jsaRacInfo.racAccessPotHazInfo = v ?? ""),
      rac_access_elim_haz_info: (v) =>
        (newJsa.jsaRacInfo.racAccessElimHazInfo = v ?? ""),
      rac_access_code: (v) => (newJsa.jsaRacInfo.racAccessCode = v ?? ""),

      rac_chemical_pot_haz_info: (v) =>
        (newJsa.jsaRacInfo.racChemicalPotHazInfo = v ?? ""),
      rac_chemical_elim_haz_info: (v) =>
        (newJsa.jsaRacInfo.racChemicalElimHazInfo = v ?? ""),
      rac_chemical_code: (v) => (newJsa.jsaRacInfo.racChemicalCode = v ?? ""),

      rac_environment_pot_haz_info: (v) =>
        (newJsa.jsaRacInfo.racEnvironmentPotHazInfo = v ?? ""),
      rac_environment_elim_haz_info: (v) =>
        (newJsa.jsaRacInfo.racEnvironmentElimHazInfo = v ?? ""),
      rac_environment_code: (v) =>
        (newJsa.jsaRacInfo.racEnvironmentCode = v ?? ""),

      rac_physical_pot_haz_info: (v) =>
        (newJsa.jsaRacInfo.racPhysicalPotHazInfo = v ?? ""),
      rac_physical_elim_haz_info: (v) =>
        (newJsa.jsaRacInfo.racPhysicalElimHazInfo = v ?? ""),
      rac_physical_code: (v) => (newJsa.jsaRacInfo.racPhysicalCode = v ?? ""),

      rac_pot_energy_pot_haz_info: (v) =>
        (newJsa.jsaRacInfo.racPotEnergyPotHazInfo = v ?? ""),
      rac_pot_energy_elim_haz_info: (v) =>
        (newJsa.jsaRacInfo.racPotEnergyElimHazInfo = v ?? ""),
      rac_pot_energy_code: (v) =>
        (newJsa.jsaRacInfo.racPotEnergyCode = v ?? ""),

      rac_tools_pot_haz_info: (v) =>
        (newJsa.jsaRacInfo.racToolsPotHazInfo = v ?? ""),
      rac_tools_elim_haz_info: (v) =>
        (newJsa.jsaRacInfo.racToolsElimHazInfo = v ?? ""),
      rac_tools_code: (v) => (newJsa.jsaRacInfo.racToolsCode = v ?? ""),

      rac_other_pot_haz_info: (v) =>
        (newJsa.jsaRacInfo.racOtherPotHazInfo = v ?? ""),
      rac_other_elim_haz_info: (v) =>
        (newJsa.jsaRacInfo.racOtherElimHazInfo = v ?? ""),
      rac_other_code: (v) => (newJsa.jsaRacInfo.racOtherCode = v ?? ""),
    };

    const toCamel = (s) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

    // Hazards/Control Measures: map snake_case to model booleans
    const setHazard = (k, v) => {
      const key = toCamel(k);
      if (Object.prototype.hasOwnProperty.call(newJsa.jsaHazardsV2, key)) {
        // Preserve string for the text field; booleanize only checkboxes
        if (k === "other_describe") {
          newJsa.jsaHazardsV2[key] = v ?? "";
        } else {
          newJsa.jsaHazardsV2[key] = Boolean(v);
        }
      }
    };
    const setCm = (k, v) => {
      const key = toCamel(k);
      if (
        Object.prototype.hasOwnProperty.call(newJsa.jsaControlMeasuresV2, key)
      ) {
        newJsa.jsaControlMeasuresV2[key] = Boolean(v);
      }
    };

    for (const field in updated_fields || {}) {
      if (basicInfoMap[field]) {
        basicInfoMap[field](updated_fields[field]);
        continue;
      }
      if (racMap[field]) {
        racMap[field](updated_fields[field]);
        continue;
      }
      // Apply to hazards and control measures by mapping snake_case → camelCase.
      // The setters are guarded by hasOwnProperty checks, so calling both is safe.
      setHazard(field, updated_fields[field]);
      setCm(field, updated_fields[field]);
    }

    return newJsa;
  }

  // Ewi Employee Signature rows.
  // Adding a blank row to the JSA employee signatures info
  addBlankJsaEmployeeSignaturesInfoRow() {
    const newRow = new JsaEmployeeSignaturesInfo();
    this.jsaEmployeeSignaturesInfo.push(newRow);
    return newRow;
  }

  // Adding all delData employees to the JSA employee signatures info
  addAllEmployeesToJsaEmployeeSignaturesInfo(employees) {
    // Setting up the existing employee ids in a set to filter out duplicates.
    const existingEmployeeIds = new Set(
      this.jsaEmployeeSignaturesInfo.map((emp) => emp.employeeId)
    );

    // Filter del.employees to only get the ones not already in our list
    const newEmployees = employees.filter(
      (employee) => !existingEmployeeIds.has(employee.employeeId)
    );

    // Add only the new employees while preserving all existing ones
    if (newEmployees.length > 0) {
      const newRows = newEmployees.map(
        (employee) =>
          new JsaEmployeeSignaturesInfo({
            employee_id: employee.employeeId,
            employee_name: employee.employeeName,
          })
      );
      this.jsaEmployeeSignaturesInfo = [
        ...this.jsaEmployeeSignaturesInfo,
        ...newRows,
      ];
      return newRows;
    }
    return [];
  }

  // Removing a row from the JSA employee signatures info
  removeJsaEmployeeSignaturesInfoRow(rowId) {
    this.jsaEmployeeSignaturesInfo = this.jsaEmployeeSignaturesInfo.filter(
      (row) => row.rowId !== rowId
    );
  }

  // Updating an employee signature row, data should come in as an object with employee_id and employee_name, and rowId
  updateJsaSignatureSelectedEmployee(employee) {
    const row = this.jsaEmployeeSignaturesInfo.find(
      (row) => row.rowId === employee.rowId
    );
    if (row) {
      row.updateSelectedEmployee(employee);
    }
    return row;
  }

  // Updating the signature
  updateJsaSignatureEmployeeSignature(signature, employeeRowId) {
    const row = this.jsaEmployeeSignaturesInfo.find(
      (row) => row.rowId === employeeRowId
    );
    if (row) {
      row.updateSignature(signature);
    }
    return row;
  }

  // Non-employee signature functions
  // Updating the non-employee name
  updateJsaSignatureNonEmployeeName(name, nonEmployeeRowId) {
    const row = this.jsaNonEmployeeSignaturesInfo.find(
      (row) => row.rowId === nonEmployeeRowId
    );
    if (row) {
      row.updateNonEmployeeName(name);
    }
    return row;
  }

  // Updating the non-employee company
  updateJsaSignatureNonEmployeeCompany(company, nonEmployeeRowId) {
    const row = this.jsaNonEmployeeSignaturesInfo.find(
      (row) => row.rowId === nonEmployeeRowId
    );
    if (row) {
      row.updateNonEmployeeCompany(company);
    }
    return row;
  }

  // Updating the non-employee signature
  updateJsaSignatureNonEmployeeSignature(signature, nonEmployeeRowId) {
    const row = this.jsaNonEmployeeSignaturesInfo.find(
      (row) => row.rowId === nonEmployeeRowId
    );
    if (row) {
      row.updateNonEmployeeSignature(signature);
    }
    return row;
  }

  // Non employee signatures functions
  // Adding a blank row to the JSA non employee signatures info
  addBlankJsaNonEmployeeSignaturesInfoRow() {
    const newRow = new JsaNonEmployeeSignaturesInfo();
    this.jsaNonEmployeeSignaturesInfo.push(newRow);
    return newRow;
  }

  // Removing a row from the JSA non employee signatures info
  removeJsaNonEmployeeSignaturesInfoRow(rowId) {
    this.jsaNonEmployeeSignaturesInfo =
      this.jsaNonEmployeeSignaturesInfo.filter((row) => row.rowId !== rowId);
  }

  // To json for sending to the database
  toJson() {
    return {
      // Core Jsa Component information
      basic_info: this.jsaBasicInfo.toJson(),
      hazard_rac_assessment: this.jsaRacInfo.toJson(),
      training: this.jsaTrainingInfo.toJson(),
      training_employees: this.jsaTrainingInfo.toJsonTrainingEmployeesInfo(),
      non_ewi_signatures: this.jsaNonEmployeeSignaturesInfo.map((sig) =>
        sig.toJson()
      ),
      signatures: this.jsaEmployeeSignaturesInfo.map((sig) => sig.toJson()),
      // Specific sections for JSA V2
      hazards: this.jsaHazardsV2.toJson(),
      control_measures: this.jsaControlMeasuresV2.toJson(),
    };
  }
}
